import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { getFieldError, getFieldErrorKeys } from '@app/common/configurable-wizards/utils';
import { CharacterRequirement } from '@app/queryTyping';
import { PoliciesComponentProps, PolicyItem, PolicyUnion } from '../types';
import { composePolicies } from '../utils/composeUsernamePasswordPolices';
import { PoliciesList } from '../common/PoliciesList';
import { PolicySettings, UsernameValidationRules } from './types';

export type UsernamePolicyItem = PolicyItem<UsernameValidationRules>;
export type UsernamePolicyUnion = PolicyUnion<UsernameValidationRules>;
export interface UsernamePoliciesProps extends PoliciesComponentProps<PolicySettings> {}

export const UsernamePolicies: React.FC<UsernamePoliciesProps> = ({
  relatedInputFocused,
  policySettings,
  fieldName,
}) => {
  const { t } = useTranslation();
  const { formState: { errors } } = useFormContext();
  const fieldError = getFieldError(fieldName, errors);

  const policies = useMemo<Array<UsernamePolicyUnion>>(() => {
    if (!policySettings) {
      return [];
    }

    const genericRequirements: Array<UsernamePolicyItem> = [];
    const crossDataRequirements: Array<UsernamePolicyItem> = [];
    const charSpecificRequirements: Array<UsernamePolicyItem> = [];
    const charSpecificProhibition: Array<UsernamePolicyItem> = [];

    // GENERIC REQUIREMENTS
    if (policySettings.anyCharacterMustBeAlphabetic) {
      genericRequirements.push({
        validationRule: UsernameValidationRules.anyAlphabetic,
        title: t(
          'username-polices.any-character-must-be-alphabetic|At least one alphabetic character requirement message',
          'At least one alphabetical letter',
        ),
      });
    }

    if (policySettings.minimumCharacterLength > 0) {
      genericRequirements.push({
        validationRule: UsernameValidationRules.minLength,
        title: t(
          'username-polices.min-character-length|Minimum username length requirement message',
          'Username must be at least {{minLength}} characters long',
          { minLength: policySettings.minimumCharacterLength },
        ),
      });
    }

    if (policySettings.maximumCharacterLength > 0) {
      genericRequirements.push({
        validationRule: UsernameValidationRules.maxLength,
        title: t(
          'username-polices.max-character-length|Up to maximum username length requirement message',
          'Username can be up to {{maxLength}} characters long',
          { maxLength: policySettings.maximumCharacterLength },
        ),
      });
    }

    // CROSS-DATA REQUIREMENTS
    if (policySettings.mustNotContainMemberNumber) {
      crossDataRequirements.push({
        title: t(
          'username-polices.cross-data-requirements.must-not-contain-mn|Must not contain MN requirement',
          'Contain your member number',
        ),
      });
    }

    if (policySettings.mustNotContainSsn) {
      crossDataRequirements.push({
        title: t(
          'username-polices.cross-data-requirements.must-not-contain-ssn|Must not contain SSN requirement',
          'Contain your social security number',
        ),
      });
    }

    if (policySettings.mustNotContainBirthdate) {
      crossDataRequirements.push({
        title: t(
          'username-polices.cross-data-requirements.must-not-contain-birthdate|Must not contain birthdate requirement',
          'Contain your birth date',
        ),
      });
    }

    if (policySettings.mustNotContainUsername) {
      crossDataRequirements.push({
        title: t(
          'username-polices.cross-data-requirements.must-not-contain-username|Must not contain real username requirement',
          'Contain another user\'s current or previous username',
        ),
      });
    }

    if (policySettings.mustNotBeContainedWithinUsername) {
      crossDataRequirements.push({
        title: t(
          'username-polices.cross-data-requirements.must-not-be-a-part-of-usernames|Must not be a part of real username requirement',
          'Be part of another user\'s current or previous username',
        ),
      });
    }

    // CHAR-SPECIFIC REQUIREMENTS/PROHIBITIONS
    if (policySettings.firstCharacterMustBeAlphabetic) {
      charSpecificRequirements.push({
        validationRule: UsernameValidationRules.firstCharAlphabetic,
        title: t(
          'username-polices.character-requirements.first-alphabetic|First letter must be alphabetic requirement',
          'First letter must be alphabetic',
        ),
      });
    }

    if (policySettings.specialCharacterRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: UsernameValidationRules.anySpecialChar,
        title: t(
          'username-polices.character-requirements.one-special-char|At least one special character requirement',
          'At least one special character',
        ),
      });
    } else if (policySettings.specialCharacterRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: UsernameValidationRules.noSpecialChar,
        title: t(
          'username-polices.character-prohibitions.special-char|Special characters prohibitance',
          'Special characters',
        ),
      });
    }

    if (policySettings.upperCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: UsernameValidationRules.anyUpperCase,
        title: t(
          'username-polices.character-requirements.one-uppercase|At least one uppercase letter requirement',
          'At least one uppercase letter (A through Z)',
        ),
      });
    } else if (policySettings.upperCaseRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: UsernameValidationRules.noUpperCase,
        title: t(
          'username-polices.character-prohibitions.uppercase|Uppercase letters prohibitance',
          'Uppercase letters (A through Z)',
        ),
      });
    }

    if (policySettings.lowerCaseRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: UsernameValidationRules.anyLowerCase,
        title: t(
          'username-polices.character-requirements.lowercase|At least one lowercase letter requirement',
          'At least one lowercase letter (a through z)',
        ),
      });
    } else if (policySettings.lowerCaseRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: UsernameValidationRules.noLowerCase,
        title: t(
          'username-polices.character-prohibitions.lowercase|Lowercase letters prohibitance',
          'Lowercase letters (a through z)',
        ),
      });
    }

    if (policySettings.numberRequirement === CharacterRequirement.ONEREQUIRED) {
      charSpecificRequirements.push({
        validationRule: UsernameValidationRules.anyNumber,
        title: t(
          'username-polices.character-requirements.one-number|At least one number requirement',
          'At least one number',
        ),
      });
    } else if (policySettings.numberRequirement === CharacterRequirement.NOTALLOWED) {
      charSpecificProhibition.push({
        validationRule: UsernameValidationRules.noNumber,
        title: t(
          'username-polices.character-prohibitions.number|Numbers prohibitance',
          'Numbers',
        ),
      });
    }

    return composePolicies<UsernameValidationRules>({
      genericRequirements,
      crossDataRequirements: {
        title: t('username-polices.cross-data-requirements|List of cross-data username requirements',
          'Your username must not:'),
        policies: crossDataRequirements,
      },
      charRequirements: {
        title: t('username-polices.character-requirements|List of character username requirements',
          'Your username must contain the following:'),
        policies: charSpecificRequirements,
      },
      charProhibitions: {
        title: t('username-polices.character-prohibitions|List of character username prohibitions',
          'Your username must not contain the following:'),
        policies: charSpecificProhibition,
      },
    });
  }, [policySettings]);

  return (
    <PoliciesList
      currentErrorKeys={getFieldErrorKeys(fieldError)}
      relatedInputFocused={relatedInputFocused}
      policies={policies}
    />
  );
};
